import { type StaticImageData } from 'next/image'

import klarnaImg from './strips/klarna_payment_methods.webp'
import klarnaLightImg from './strips/klarna_payment_methods_light.png'
import nexiImg from './strips/nexi_payment_methods.png'
import sveaImg from './strips/svea_payment_methods_dark.png'
import sveaLightImg from './strips/svea_payment_methods_light.png'
// https://developers.qliro.com/docs/on-site-material/qliro-payment-badge
import qliroImg from './strips/qliro_sv_alt6_flat_dark_payment_methods.png'
import qliroLightImg from './strips/qliro_sv_alt6_flat_light_payment_methods.png'

/**
 * Ready-made strips published by a payment provider, showing the methods that provider
 * accepts in one image.
 *
 * Distinct from {@link PaymentMethodId}: these are the provider's own on-site material,
 * not a mark for a single method, so they replace the whole row rather than sitting in
 * it. They are the same files the footer has always used as its per-provider fallback.
 */
export type PaymentStripId = 'klarna' | 'nexi' | 'svea' | 'qliro'

export interface PaymentStripAsset {
  /** Used on light backgrounds. */
  colour: StaticImageData
  /** Light-coloured variant for dark backgrounds; falls back to `colour`. */
  light?: StaticImageData
  label: string
}

export const PAYMENT_STRIP_ASSETS: Record<PaymentStripId, PaymentStripAsset> = {
  klarna: { colour: klarnaImg, light: klarnaLightImg, label: 'Klarna payment methods' },
  // Nexi publishes a single treatment, so it keeps its colour strip on dark footers.
  nexi: { colour: nexiImg, label: 'Nexi payment methods' },
  svea: { colour: sveaImg, light: sveaLightImg, label: 'Svea payment methods' },
  qliro: { colour: qliroImg, light: qliroLightImg, label: 'Qliro payment methods' }
}

/** The bundled strip for a surface, honouring its contrast. */
export const resolvePaymentStrip = (
  id: PaymentStripId | undefined,
  isDarkSurface: boolean
): StaticImageData | undefined => {
  const asset = id ? PAYMENT_STRIP_ASSETS[id] : undefined

  if (!asset) {
    return undefined
  }

  return isDarkSurface ? (asset.light ?? asset.colour) : asset.colour
}
